import {
  Auth0ContextInterface,
  Auth0Provider,
  User,
  useAuth0 as useAuth0Auth0,
} from "@auth0/auth0-react";
import { AuthenticationHandler } from "App/AuthenticationHandler";
import { format } from "date-fns";
import { atom, useAtom } from "jotai";
import { Context, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMount } from "react-use";
import { FORMAT_READABLE_WEEK_MONTH_TIME } from "utils/timeUtils";
import { decodeToken } from "./jwtHelper";
import useFlagsWithDefaults, {
  hasLDClientBeenIdentifiedAtom,
} from "utils/customHooks/useFlagsWithDefaults";
import { atomWithStorage } from "jotai/utils";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";

export const shouldEnableRefreshTokensAtom = atomWithStorage(
  "shouldEnableRefreshTokens",
  false
);

export const Auth0Manager = ({
  children,
  skipAuth,
  mockUser,
}: {
  children: React.ReactNode;
  /** we can skipAuth for convenience in some storybook stories (test environment) like Nav/App/Pages.stories.tsx */
  skipAuth?: boolean;
  mockUser?: User | null;
}) => {
  const navigate = useNavigate();

  const [shouldEnableRefreshTokensLS] = useAtom(shouldEnableRefreshTokensAtom);

  return (
    <Auth0Provider
      domain="raventelemetry.auth0.com"
      clientId="o3NNxjvHrgEB8sLg3zX8r1PwVHPeU7YC"
      authorizationParams={{
        scope: `openid profile${
          shouldEnableRefreshTokensLS ? " offline_access" : ""
        }`,
        response_type: "token id_token",
        redirect_uri: `${window.location.protocol}//${window.location.host}/callback/`,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={shouldEnableRefreshTokensLS}
      onRedirectCallback={appState => {
        if (appState?.targetUrl) {
          // navigate back to targetUrl (where we logged out from) without adding to history
          navigate(appState.targetUrl, {
            replace: true,
          });
        }
      }}
    >
      <RefreshTokensSettingManager />
      {skipAuth ? null : <AuthenticationHandler />}
      {mockUser ? <UserMocker mockUser={mockUser} /> : null}
      {children}
    </Auth0Provider>
  );
};

const mockUserAtom = atom<User | null>(null);

function UserMocker({ mockUser }: { mockUser: User }) {
  const [, setUser] = useAtom(mockUserAtom);
  useMount(() => {
    setUser(mockUser);
    return () => setUser(null);
  });
  return null;
}

export const useAuth0 = (
  context?: Context<Auth0ContextInterface<User>> | undefined
): Auth0ContextInterface<User> & {
  user: User & {
    app_metadata?: { customer?: string[] };
    customer?: string;
  };
} => {
  const [mockUser] = useAtom(mockUserAtom);
  const authContext = useAuth0Auth0(context);
  return useMemo(() => {
    const token = getTokenInfo();
    const idTokenDecoded = token.idToken && decodeToken(token.idToken);
    if (mockUser) {
      return { ...authContext, user: mockUser, isAuthenticated: true };
    }
    const email =
      authContext.user?.email ??
      authContext.user?.["https://raven.ai/email"] ??
      idTokenDecoded?.["https://raven.ai/email"];
    const app_metadata =
      authContext.user?.app_metadata ??
      authContext.user?.["https://raven.ai/app_metadata"] ??
      idTokenDecoded?.["https://raven.ai/app_metadata"];
    const customer =
      authContext.user?.customer ??
      authContext.user?.["https://raven.ai/customer"] ??
      authContext.user?.["https://raven.ai/app_metadata"]?.customer ??
      app_metadata?.customer ??
      idTokenDecoded?.["https://raven.ai/customer"];
    return {
      ...authContext,
      user: { ...authContext.user, email, app_metadata, customer },
    };
  }, [authContext, mockUser]);
};
/**
 * If we're running in a review app (via GitLab),
 * we want to display the login component from <Provisioning />
 * and we don't want to show the Auth0 login screen.
 */
const isReviewApp = process.env.REACT_APP_REVIEW === "true";

/**
 * Need to map localStorage keys for review apps
 * as the provisioning library uses slightly different keys.
 */
const storageKeys = isReviewApp
  ? {
      id_token: "id_token",
      token_expiry: "expires_at",
    }
  : { id_token: "id_token", token_expiry: "token_expiry" };

/**
 * returns info about the id_token and token_expiry in localStorage
 */
export function getTokenInfo(): {
  isTokenValid: boolean;
  isTokenExpiryInFuture: boolean;
  idToken: string | null;
  idTokenExpiryMs: number;
  idTokenExpFormatted: string | null;
  clearTokenFromLocalStorage: () => void;
  saveTokenInLocalStorage: (token: string, expiry: string | null) => void;
} {
  const idToken = window.localStorage.getItem(storageKeys.id_token);
  const idTokenExpiry = window.localStorage.getItem(storageKeys.token_expiry);
  const wasBadTokenCleared = window.localStorage.getItem(
    "last_token_was_invalid"
  );
  const idTokenExpiryMs = Number(idTokenExpiry);
  const isTokenExpiryInFuture = Boolean(
    idTokenExpiryMs && Date.now() < idTokenExpiryMs
  );
  const isTokenValid = Boolean(
    idToken &&
      isTokenExpiryInFuture &&
      // token is invalidated if we clear it using clearTokenFromLocalStorage
      !wasBadTokenCleared
  );
  const idTokenExpFormatted = idTokenExpiryMs
    ? format(idTokenExpiryMs, FORMAT_READABLE_WEEK_MONTH_TIME)
    : null;

  /** remove id_token and token_expiry, and set last_token_was_invalid to invalidate using the token from local storage */
  const clearTokenFromLocalStorage = () => {
    window.localStorage.removeItem(storageKeys.id_token);
    window.localStorage.removeItem(storageKeys.token_expiry);
    window.localStorage.setItem("last_token_was_invalid", "true");
  };

  /** save the new id_token in local storage and remove the last_token_was_invalid flag */
  const saveTokenInLocalStorage = (token: string, expiry: string | null) => {
    window.localStorage.setItem(storageKeys.id_token, token);
    window.localStorage.removeItem("last_token_was_invalid");
    if (expiry) {
      window.localStorage.setItem(storageKeys.token_expiry, expiry);
    }
  };

  return {
    isTokenValid,
    isTokenExpiryInFuture,
    idToken,
    idTokenExpiryMs,
    idTokenExpFormatted,
    clearTokenFromLocalStorage,
    saveTokenInLocalStorage,
  };
}

/**
 * Refresh tokens allow users to stay logged in permanently, but they cause problems for some users who use their own custom login portals.
 * We would like to enable them for all users, but for now, we need to target specific users using a feature flag (launchdarkly).
 * However... we can't identify/target users in launchdarkly before they log in.
 * So, we need to always start with refresh tokens disabled, then enable them after the user logs in, and store this config in localStorage so it's present during their next login.
 * If this starts to cause problems and the user can't stay logged-in with refresh tokens enabled, after disabling the flag for them we may also need to clear the localStorage item with flag "clear local storage item" (https://app.launchdarkly.com/default/production/features/flight-clear-local-storage-item/targeting) by setting it to "shouldEnableRefreshTokens"
 */
function useSetShouldEnableRefreshTokensLS() {
  const [shouldEnableRefreshTokensLS, setShouldEnableRefreshTokensLS] = useAtom(
    shouldEnableRefreshTokensAtom
  );
  const { flightEnableRefreshTokensLs } = useFlagsWithDefaults();
  const [hasLDClientBeenIdentified] = useAtom(hasLDClientBeenIdentifiedAtom);
  const { user } = useAuth0();
  const ckey = useCustomerKey();
  const ready =
    hasLDClientBeenIdentified &&
    flightEnableRefreshTokensLs !== null &&
    user.email &&
    ckey;
  useEffect(() => {
    if (!ready) {
      return;
    }
    if (shouldEnableRefreshTokensLS !== flightEnableRefreshTokensLs) {
      setShouldEnableRefreshTokensLS(flightEnableRefreshTokensLs);
    }
  }, [
    ready,
    flightEnableRefreshTokensLs,
    shouldEnableRefreshTokensLS,
    setShouldEnableRefreshTokensLS,
  ]);
}
/** need to render this inside the Auth0Manager to access the user */
function RefreshTokensSettingManager() {
  useSetShouldEnableRefreshTokensLS();
  return null;
}
